import { useState, useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function LongevityAntiAgingPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-12 overflow-hidden bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#7C3AED]/20">
                <div className="w-2 h-2 bg-[#7C3AED] rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Longevity & Anti-Aging Medicine</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                <span className="text-gray-900">Marketing for</span>
                <br />
                <span className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent">Longevity Medicine</span>
                <br />
                <span className="text-gray-900">Clinics</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed">
                Specialized digital marketing for longevity and anti-aging medicine practices. We help you attract affluent patients seeking cutting-edge treatments, healthspan optimization, and age-reversal therapies through sophisticated HIPAA-compliant campaigns.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:from-[#6D28D9] hover:to-[#9333EA] transition-all hover:shadow-lg cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group font-medium"
                >
                  <span>Book Strategy Call</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a
                  href="/automation-platform"
                  className="border-2 border-[#7C3AED] text-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED] hover:text-white transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  See Our Platform
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/bb024baec69bfa6615033c08586f64b8.jpg"
                  alt="Longevity Medicine Clinic"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              UNIQUE CHALLENGES
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-6">
              Marketing Challenges for Longevity Medicine
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Longevity medicine requires reaching affluent, health-conscious patients and communicating complex scientific concepts effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-vip-crown-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">High-Value Patient Targeting</h3>
              <p className="text-gray-600 leading-relaxed">
                Reaching affluent patients willing to invest in premium longevity treatments requires sophisticated targeting and positioning.
              </p>
            </div>

            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-microscope-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Complex Science Communication</h3>
              <p className="text-gray-600 leading-relaxed">
                Explaining advanced therapies like NAD+ IV, peptides, and senolytics in accessible yet credible ways.
              </p>
            </div>

            <div className="bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-[#7C3AED] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Regulatory Compliance</h3>
              <p className="text-gray-600 leading-relaxed">
                Navigating advertising restrictions for emerging therapies while maintaining HIPAA compliance and medical ethics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=longevity%20medicine%20doctor%20consultation%20advanced%20biomarker%20testing%20personalized%20anti-aging%20treatment%20plan%20modern%20wellness%20clinic%20luxury%20healthcare%20setting%20professional%20medical%20consultation&width=700&height=600&seq=longevity-solution1&orientation=landscape"
                  alt="Longevity Medicine Solutions"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                  OUR SOLUTIONS
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-6">
                  Specialized Marketing for Longevity Clinics
                </h2>
              </div>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-star-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Affluent Audience Targeting</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Precision targeting of high-net-worth individuals through premium channels, executive networks, and luxury lifestyle platforms.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-article-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Science-Based Content Marketing</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Educational content that explains longevity science, biomarkers, and treatment protocols with clinical credibility and accessibility.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-trophy-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Brand Positioning</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Sophisticated branding and messaging that positions your clinic as the premier destination for cutting-edge longevity medicine.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">VIP Lead Nurturing</h3>
                    <p className="text-gray-600 leading-relaxed">
                      White-glove automated sequences that educate prospects about longevity protocols and guide them to high-value consultations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              PROVEN RESULTS
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
              Real Growth for Longevity Medicine Practices
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                +320%
              </div>
              <div className="text-gray-900 font-bold mb-2">High-Value Consultations</div>
              <div className="text-sm text-gray-600">Premium patient inquiries increased</div>
            </div>

            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                $18K
              </div>
              <div className="text-gray-900 font-bold mb-2">Average Patient Value</div>
              <div className="text-sm text-gray-600">First-year revenue per patient</div>
            </div>

            <div className="text-center p-8 bg-[#F9FAFB] rounded-xl">
              <div className="text-5xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent mb-3">
                4.8x
              </div>
              <div className="text-gray-900 font-bold mb-2">ROI on Marketing</div>
              <div className="text-sm text-gray-600">Average across longevity clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Grow Your Longevity Medicine Practice?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Let's discuss how our specialized marketing strategies can help you attract high-value patients seeking cutting-edge longevity treatments.
          </p>
          <a
            href="/book-strategy-call"
            className="bg-white text-[#0D9488] px-10 py-5 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group font-bold text-lg shadow-xl"
          >
            <span>Schedule Your Free Strategy Call</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>
      </section>

      <RelatedServices currentSlug="longevity-antiaging" />
      <RelatedIndustries currentSlug="longevity-antiaging" />
      <Footer />
    </div>
  );
}
